// Infinite loader for collection pages
class InfinitePageLoader extends HTMLElement {
  constructor() {
    super();

    // Get the section ID for rendering via fetch
    this.sectionID =
      this.dataset.section ||
      document.querySelector("infinite-page-loader")?.dataset.section ||
      "";

    // Keep track of sold out products to show later
    this.soldOutProduct = [];

    // Main collection grid element
    this.collectionInnerElement = document.querySelector(".new-grid.product-grid.collection-grid");

    if (!this.collectionInnerElement) {
      console.warn('[InfinitePageLoader] Expected collection grid not found – aborting initialisation.');
      return;
    }

    // Wrapper that holds config for grid layout
    const collectionGridWrapper = document.querySelector('.collection-grid__wrapper');

    if (!collectionGridWrapper) {
      console.warn('[InfinitePageLoader] Collection wrapper not found – aborting initialisation.');
      return;
    }

    // Number of products shown before inserting filter component
    const desktopGridRowsProductCount = parseInt(collectionGridWrapper.dataset.desktopRow) * 4;
    const mobileGridRowsProductCount = parseInt(collectionGridWrapper.dataset.mobileRow) * 2;
    
    const applyCustomDesktopFilters = collectionGridWrapper.dataset.isDesktop;
    const applyCustomMobileFilters = collectionGridWrapper.dataset.isMobile;

    if (applyCustomDesktopFilters === 'false'){
      this.applyDesktopFilters = false;
    }
    else if(applyCustomDesktopFilters === 'true'){
      this.applyDesktopFilters = true;
    }

    if (applyCustomMobileFilters === 'false'){
      this.applyMobileFilters = false;
    }
    else if(applyCustomMobileFilters === 'true'){
      this.applyMobileFilters = true;
    }

    // Save and restore scroll position
    this.saveScrollPoint();
    this.restoreScrollPoint();

    // Position to inject filters based on screen
    this.mobileFiltersPosition = mobileGridRowsProductCount;
    this.desktopFiltersPosition = desktopGridRowsProductCount;
  }

  // Called when the component is inserted into the DOM
  connectedCallback() {
    this.init();
  }

  // Initialize loader
  init() {
    // Add intersection observer for lazy loading
    this.addObserver();

    // On first load, remove sold-out products and save them for later
    if (this.dataset.currentpage == 1 && this.dataset.currentpage != this.dataset.totalpages) {
      this.collectionInnerElement.querySelectorAll(".sold-out-product").forEach((soldout) => {
        this.soldOutProduct.push(soldout);
        soldout.remove();
      });
    }

    this.remainingProducts = [];

    // Insert filters based on current layout
    this.handleDevice();
  }

  // Determine mobile vs desktop and set filter positions accordingly
  handleDevice() {
    if (window.innerWidth <= 749) {
      this.insertFiltersInPreviousProducts(this.mobileFiltersPosition);
    } else {
      this.insertFiltersInPreviousProducts(this.desktopFiltersPosition);
    }
  }

  // Set up IntersectionObserver to trigger loading next page
  addObserver() {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (entry.target === this.loadActivatorElem) observer.disconnect();
          var nextPageUrl = this.getNextPageUrl();
          if (nextPageUrl) this.loadProducts(nextPageUrl);
        }
      },
      {
        root: null,
        threshold: 0.1, // When 10% of the loader is visible
      }
    );
    observer.observe(this);
  }

  // Build next page URL
  getNextPageUrl() {
    var nextPage = parseInt(this.dataset.currentpage) + 1;
    if (nextPage > this.dataset.totalpages) return false;
    return this.constructURLWithSearchParams(nextPage);
  }

  // Append current URL with updated page param
  constructURLWithSearchParams(page) {
    var params = new URLSearchParams(document.location.search);
    var nextPage = parseInt(page) || parseInt(this.dataset.currentpage) + 1;
    params.set("page", nextPage);
    return `${window.location.pathname}?${params.toString()}`;
  }

  // Update browser address bar without reloading page
  updateCurrentURL(url) {
    if (history.replaceState) {
      window.history.pushState({ path: url }, "", url);
    }
  }

  // Load product HTML from next page
  loadProducts(url) {
    if (this.classList.contains("loading")) return;
    this.classList.add("loading");
    this.getAndInsertHtml(url, "append");
  }

  /**
   * Inserts filters into the current product grid based on given position
   */
  insertFiltersInPreviousProducts(filterPosition) {
    const allProducts = Array.from(this.collectionInnerElement.querySelectorAll(".grid-item.grid-product"));
    if (allProducts.length <= filterPosition) return;

    const isMobile = window.innerWidth <= 767;
    const shouldApplyFilters = (!isMobile && this.applyDesktopFilters) || (isMobile && this.applyMobileFilters);

    if (!shouldApplyFilters) {
      // Just show all products without filters
      this.collectionInnerElement.innerHTML = '';
      allProducts.forEach(product => this.collectionInnerElement.appendChild(product));
      return;
    }

    const filterTemplate = document.querySelector('.testing-custom-filter--desktop');
    const filterClone = () => {
      const clone = filterTemplate.cloneNode(true);
      clone.classList.remove('hidden');
      return clone;
    };

    const firstChunk = allProducts.slice(0, filterPosition);
    const restProducts = allProducts.slice(filterPosition);

    this.collectionInnerElement.innerHTML = ''; // Clear existing products

    // Append first chunk + filter
    firstChunk.forEach(product => this.collectionInnerElement.appendChild(product));
    this.collectionInnerElement.appendChild(filterClone());

    const collectionContainer = document.querySelector('[data-section-type=collection-template]');
    if (collectionContainer) new theme.Collection(collectionContainer);

    // Append rest of products in chunks with filters
    this.remainingProducts = [];
    for (let i = 0; i < restProducts.length; i += filterPosition) {
      const chunk = restProducts.slice(i, i + filterPosition);
      chunk.forEach(product => this.collectionInnerElement.appendChild(product));
      if (chunk.length === filterPosition) {
        this.collectionInnerElement.appendChild(filterClone());
      } else {
        this.remainingProducts = chunk;
      }
    }
  }

  /**
   * Handles adding new products after loading a page
   */
  handleAjaxResponse(newProducts, filterPosition) {
    const newProductArray = Array.from(newProducts);
    const combined = this.remainingProducts.concat(newProductArray);
    this.remainingProducts = [];

    const isMobile = window.innerWidth <= 767;
    const shouldApplyFilters = (!isMobile && this.applyDesktopFilters) || (isMobile && this.applyMobileFilters);

    if (!shouldApplyFilters) {
      // Just show all products without filters
      combined.forEach(product => this.collectionInnerElement.appendChild(product));
      return;
    }

    const filterTemplate = document.querySelector('.testing-custom-filter--desktop');
    const filterClone = () => {
      const clone = filterTemplate.cloneNode(true);
      clone.classList.remove('hidden');
      return clone;
    };

    for (let i = 0; i < combined.length; i += filterPosition) {
      const chunk = combined.slice(i, i + filterPosition);
      chunk.forEach(prod => this.collectionInnerElement.appendChild(prod));

      if (chunk.length === filterPosition) {
        this.collectionInnerElement.appendChild(filterClone());
      } else {
        this.remainingProducts = chunk;
      }
    }
    const collectionContainer = document.querySelector('[data-section-type=collection-template]');
    if (collectionContainer) new theme.Collection(collectionContainer);
  }

  /**
   * Fetch HTML from URL and inject it into DOM
   */
  getAndInsertHtml(url, insertPosition) {
    if (this.abortController) this.abortController.abort();

    const currentUrl = new URL(url, window.location.href);
    currentUrl.searchParams.set("section_id", this.sectionID);

    this.abortController = new AbortController();

    fetch(currentUrl.toString(), { signal: this.abortController.signal }).then((response) => {
      response.text().then((content) => {
        const tempElement = new DOMParser().parseFromString(content, "text/html");

        if (insertPosition === "append") {
          // Remove sold-out products from new HTML
          tempElement.querySelector(".new-grid.product-grid.collection-grid").querySelectorAll(".sold-out-product").forEach((soldout) => {
            this.soldOutProduct.push(soldout);
            soldout.remove();
          });

          const newProducts = tempElement.querySelectorAll('.grid-item.grid-product');

          // Recalculate based on device
          if (window.innerWidth <= 749) {
            this.insertFiltersInPreviousProducts(this.mobileFiltersPosition);
            this.handleAjaxResponse(newProducts, this.mobileFiltersPosition);
          } else {
            this.insertFiltersInPreviousProducts(this.desktopFiltersPosition);
            this.handleAjaxResponse(newProducts, this.desktopFiltersPosition);
          }

          // Update current/total page data attributes
          this.dataset.currentpage = tempElement.querySelector("infinite-page-loader").dataset.currentpage;
          this.dataset.totalpages = tempElement.querySelector("infinite-page-loader").dataset.totalpages;

          // Once final page is reached, append sold-out products
          if (this.dataset.currentpage == this.dataset.totalpages) {
            this.soldOutProduct.forEach((soldOutProduct) => {
              this.collectionInnerElement.insertAdjacentHTML("beforeend", soldOutProduct.outerHTML);
            });
          }

        } else if (insertPosition === "prepend") {
          this.collectionInnerElement.insertAdjacentHTML(
            "afterbegin",
            tempElement.querySelector(".new-grid.product-grid.collection-grid").innerHTML
          );
          this.dataset.currentpage = tempElement.querySelector("load-previous-page").dataset.currentpage;
        }

        // Reinitialize state and scroll tracking
        this.init();
        this.saveScrollPoint();
        this.classList.remove("loading");
        document.dispatchEvent(new CustomEvent('infiniteContentLoaded'));
      });
    });
  }

  /**
   * Track scroll position and update browser URL as you scroll through products
   */
  saveScrollPoint() {
    let productCards = document.querySelectorAll(".product-card-wrapper");

    productCards.forEach((productCard) => {
      const observer = new IntersectionObserver(
        (entries) => {
          observerCallback(entries, observer, productCard);
        },
        { threshold: 0.7 }
      );
      observer.observe(productCard);

      // Save ID when clicked (for restoring later)
      productCard.addEventListener("click", () => {
        sessionStorage.setItem("lastElementId", productCard.id);
      });
    });

    // Update URL when product is in view
    const observerCallback = (entries, observer, header) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.updateCurrentURL(
            this.constructURLWithSearchParams(entry.target.dataset.page)
          );
        }
      });
    };
  }

  // Scroll to previously viewed product if available
  restoreScrollPoint() {
    if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) return;
    history.scrollRestoration = "manual";

    window.onload = function () {
      var lastElementId = sessionStorage.getItem("lastElementId");
      var lastElement = document.getElementById(lastElementId);
      if (lastElement) lastElement.scrollIntoView();
      sessionStorage.removeItem("lastElementId");
    };
  }
}

customElements.define("infinite-page-loader", InfinitePageLoader);


// Extend the above class to support loading previous pages on button click
class LoadPreviousPage extends InfinitePageLoader {
  constructor() {
    super();
    this.addEventListener("click", this.loadProducts.bind(this));
  }

  connectedCallback() {
    this.init();
  }

  // Hide or show the "load previous" button based on current page
  init() {
    var infinitePageLoader = document.querySelector("infinite-page-loader");
    var maxPagesLoadable = this.dataset.totalpages - 1;

    if (
      infinitePageLoader.dataset.currentpage - this.dataset.currentpage == maxPagesLoadable ||
      this.dataset.currentpage == 1
    ) {
      this.classList.add("hidden");
    } else {
      this.classList.remove("hidden");
    }
  }

  // Load previous page on click
  loadProducts() {
    if (this.classList.contains("loading")) return;

    var prevPageUrl = this.getPrevPageUrl();
    if (prevPageUrl) {
      this.classList.add("loading");
      this.getAndInsertHtml(prevPageUrl, "prepend");
    }
  }

  // Build previous page URL
  getPrevPageUrl() {
    var prevPage = parseInt(this.dataset.currentpage) - 1;
    if (prevPage < 1) return false;
    return this.constructURLWithSearchParams(prevPage);
  }
}

customElements.define("load-previous-page", LoadPreviousPage);
